import { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Check, X, MapPin } from 'lucide-react';
import { getPodConfig, qrPostPublic, passportTokenFrom } from './qrPublicApi';

const IDLE_MS = 10 * 60 * 1000;

/**
 * Continuous passport scanner. BarcodeDetector when the browser has it,
 * html5-qrcode (ZXing) fallback otherwise. An always-visible manual-entry input
 * accepts either a 20-char token or an 8-char short code — backend tags typed
 * binds as `TYPED`, scanned ones as `SCAN`; the tick list shows the TYPED badge.
 */
export function BindScanner({ deviceToken, trkId, onDone }) {
  const [ticks, setTicks] = useState([]);
  const [status, setStatus] = useState('Point the camera at a passport label');
  const [geo, setGeo] = useState(undefined); // undefined = not asked yet, null = denied
  const [manual, setManual] = useState('');
  const seen = useRef(new Set());
  const busy = useRef(false);
  const idle = useRef(null);
  const videoRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const resetIdle = useCallback(() => {
    clearTimeout(idle.current);
    idle.current = setTimeout(() => onDoneRef.current('idle'), IDLE_MS);
  }, []);

  const getGeo = useCallback(() => new Promise(resolve => {
    if (geo !== undefined) return resolve(geo);
    if (!navigator.geolocation) { setGeo(null); return resolve(null); }
    navigator.geolocation.getCurrentPosition(
      pos => { const g = { lat: pos.coords.latitude, lon: pos.coords.longitude }; setGeo(g); resolve(g); },
      () => { setGeo(null); resolve(null); }, { timeout: 8000 });
  }), [geo]);

  const handle = useCallback(async (text, manualShort = null) => {
    const token = manualShort ? null : passportTokenFrom(text);
    const key = token || manualShort;
    if (!key || busy.current) return;
    resetIdle();
    if (seen.current.has(key)) { setStatus('Already here'); return; }
    busy.current = true;
    try {
      const g = await getGeo();
      const cfg = await getPodConfig();
      if (cfg.location_required && !g) { setStatus('Location required — allow GPS to bind'); busy.current = false; return; }
      const r = await qrPostPublic('/bind', { device_token: deviceToken, passport_token: token, passport_short_code: manualShort, client_request_id: crypto.randomUUID(), lat: g?.lat ?? null, lon: g?.lon ?? null });
      seen.current.add(key);
      if (r.already_here) { setStatus(`${r.short_code} — already here`); return; }
      setTicks(t => [{ code: r.short_code, moved: r.moved_from, kids: r.children_bound, id: r.binding_id, method: r.bind_method || (manualShort ? 'TYPED' : 'SCAN') }, ...t]);
      setStatus(`${r.short_code} bound${r.bind_method === 'TYPED' ? ' (typed)' : ''}${r.moved_from ? ` — moved from ${r.moved_from}` : ''}${r.children_bound ? ` (+${r.children_bound} in carton)` : ''}`);
      if (navigator.vibrate) navigator.vibrate(60);
    } catch (e) {
      setStatus(e.message);
    } finally {
      setTimeout(() => { busy.current = false; }, 700);
    }
  }, [deviceToken, getGeo, resetIdle]);

  useEffect(() => {
    resetIdle();
    let stop = () => {};
    const useNative = 'BarcodeDetector' in window;
    (async () => {
      if (useNative) {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        let alive = true;
        const loop = async () => {
          if (!alive) return;
          try { const codes = await detector.detect(video); if (codes[0]) await handle(codes[0].rawValue); } catch { /* frame skipped */ }
          setTimeout(loop, 250);
        };
        loop();
        stop = () => { alive = false; stream.getTracks().forEach(t => t.stop()); };
      } else {
        const scanner = new Html5Qrcode('bind-reader');
        await scanner.start({ facingMode: 'environment' }, { fps: 8, qrbox: 220 }, (t) => handle(t), () => {})
          .catch(e => setStatus(`Camera unavailable (${e?.message || e}) — type the passport code below`));
        stop = () => { try { if (scanner.isScanning) scanner.stop().catch(() => {}); } catch { /* not running */ } };
      }
    })().catch(e => setStatus(`Camera unavailable (${e.message || e}) — type the passport code below`));
    return () => { stop(); clearTimeout(idle.current); };
  }, [handle, resetIdle]);

  const submitManual = (e) => {
    e.preventDefault();
    const v = manual.trim().toUpperCase();
    if (!v) return;
    setManual('');
    if (passportTokenFrom(v)) handle(v); else handle(null, v);   // 20-char token or 8-char short code
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 text-white flex flex-col" data-testid="bind-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-sm">
        <span>Binding to <span className="font-mono font-bold">{trkId}</span></span>
        <span className="flex items-center gap-1 text-xs text-slate-400"><MapPin size={12} />{geo === undefined ? 'location on first bind' : geo ? 'located' : 'no location'}</span>
      </div>
      <div className="relative mx-auto w-full max-w-sm aspect-square bg-black rounded-xl overflow-hidden">
        {'BarcodeDetector' in window ? <video ref={videoRef} className="w-full h-full object-cover" muted playsInline /> : <div id="bind-reader" className="w-full h-full" />}
        <div className="absolute inset-8 border-2 border-emerald-400/70 rounded-lg pointer-events-none" />
      </div>
      <p className="text-center text-sm px-4 py-3 text-emerald-300" data-testid="bind-status">{status}</p>
      <form onSubmit={submitManual} className="mx-auto w-full max-w-sm px-4 pb-2 flex gap-2" data-testid="bind-manual">
        <input value={manual} onChange={e => setManual(e.target.value.toUpperCase())} placeholder="Type passport code (e.g. 7KQ2M9XA)" aria-label="Passport short code" autoCapitalize="characters" spellCheck={false}
          className="flex-1 h-10 rounded-md bg-slate-900 border border-slate-700 px-3 font-mono text-sm text-white placeholder:text-slate-500" data-testid="bind-manual-input" />
        <button type="submit" disabled={!manual.trim()} className="h-10 px-4 rounded-md bg-emerald-500 text-slate-950 font-semibold text-sm disabled:opacity-40" data-testid="bind-manual-submit">Bind</button>
      </form>
      <ul className="flex-1 overflow-y-auto px-4 space-y-1 text-sm" data-testid="bind-ticks">
        {ticks.map(t => (
          <li key={t.id} className="flex items-center gap-2 bg-slate-900 rounded px-3 py-2">
            <Check size={16} className="text-emerald-400" />
            <span className="font-mono font-semibold">{t.code}</span>
            {t.method === 'TYPED' && <span className="text-[10px] bg-amber-500/20 text-amber-300 rounded px-1.5 py-0.5">TYPED</span>}
            {t.moved && <span className="text-xs text-amber-300">moved from {t.moved}</span>}
            {t.kids > 0 && <span className="text-xs text-slate-400">+{t.kids} children</span>}
          </li>
        ))}
      </ul>
      <button data-testid="bind-done" onClick={() => onDone('done')} className="m-4 rounded-full bg-white text-slate-900 py-3 font-bold flex items-center justify-center gap-2">
        <X size={16} /> Done ({ticks.length} bound)
      </button>
    </div>
  );
}
